import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconAtom2Outline18 } from "nucleo-ui-outline-18"

import { AI_SPEND_LIMIT_TRANSLATE_MESSAGE } from "@productlane/lib"
import { AlertDialog, Button, Tooltip } from "@productlane/ui"

import type { TranslationTarget } from "@/lib/translation-status"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useTranslationTargetsSummary } from "@/lib/translation-status"
import { formatLanguageList } from "@/lib/translation-targets"

interface TranslateWithAIButtonProps {
	resource: "helpCenterArticle" | "changelog"
	resourceId: string
	// Every enabled language except the default, with its live (Zero-synced)
	// translation row, so a run still in flight shows progress after navigating back.
	targets: readonly TranslationTarget[]
}

function languageCount(count: number): string {
	return `${count} ${count === 1 ? "language" : "languages"}`
}

export function TranslateWithAIButton({
	resource,
	resourceId,
	targets,
}: TranslateWithAIButtonProps) {
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const navigate = useNavigate()

	const translateHelpCenterArticle =
		api.ai.translateHelpCenterArticleToAllLanguages.useMutation()
	const translateChangelog =
		api.ai.translateChangelogToAllLanguages.useMutation()

	const {
		isRunning,
		failedLanguageCodes,
		retryLanguageCodes,
		overwrittenLanguageNames,
	} = useTranslationTargetsSummary(targets)
	const isFailed = !isRunning && failedLanguageCodes.length > 0

	if (targets.length === 0) return null

	// A retry skips finished languages, so they keep their text.
	const runTranslation = async (targetLanguages?: string[]) => {
		try {
			const { targetLanguages: queued } =
				resource === "helpCenterArticle"
					? await translateHelpCenterArticle.mutateAsync({
							articleId: resourceId,
							targetLanguages,
						})
					: await translateChangelog.mutateAsync({
							changelogId: resourceId,
							targetLanguages,
						})
			analytics.track(
				resource === "helpCenterArticle"
					? "helpcenterarticle-translate"
					: "changelog-translate",
				{ id: resourceId, targetLanguages: queued },
			)
			toast.info({
				title: `Translating into ${languageCount(queued.length)}`,
				description: "This runs in the background. You can leave this page.",
			})
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Please try again in a moment."
			// A spent budget is a guard the user can lift, not a failure, so it
			// gets the neutral toast and a way to the usage page.
			if (message === AI_SPEND_LIMIT_TRANSLATE_MESSAGE) {
				toast.info({
					title: "AI spend limit reached",
					description: message,
					action: {
						label: "Manage AI usage",
						callback: () => void navigate({ to: "/settings/ai/usage" }),
					},
				})
				return
			}
			toast.error({
				title: "Could not start translation",
				description: message,
			})
		}
	}

	const handleClick = () => {
		if (isRunning) return
		if (isFailed) {
			void runTranslation(retryLanguageCodes)
			return
		}
		if (overwrittenLanguageNames.length > 0) {
			setIsConfirmOpen(true)
			return
		}
		void runTranslation()
	}

	const label = isRunning
		? "Translating..."
		: isFailed
			? "Retry translation"
			: "Translate all languages"

	return (
		<>
			<AlertDialog
				isOpen={isConfirmOpen}
				onOpenChange={setIsConfirmOpen}
				title="Overwrite existing translations?"
				description={`${formatLanguageList(overwrittenLanguageNames)} already ${
					overwrittenLanguageNames.length === 1 ? "has" : "have"
				} content in this ${
					resource === "changelog" ? "changelog" : "article"
				}. Translating from the default language will replace it.`}
				confirmText="Translate & Replace"
				cancelText="Cancel"
				variant="negative"
				onConfirm={() => {
					setIsConfirmOpen(false)
					void runTranslation()
				}}
				onCancel={() => setIsConfirmOpen(false)}
			/>
			<Tooltip
				label={
					isFailed
						? `Retry ${formatLanguageList(
								targets
									.filter((target) => retryLanguageCodes.includes(target.code))
									.map((target) => target.name),
							)}`
						: `Translate the default language into ${formatLanguageList(
								targets.map((target) => target.name),
							)} with AI`
				}
				enabled={!isRunning}
			>
				<>
					<style>
						{`
						@keyframes translate-shimmer {
							0% { background-position: -200% 0; }
							100% { background-position: 200% 0; }
						}
					`}
					</style>
					<Button
						variant="outline"
						Icon={IconAtom2Outline18}
						iconStyle={isFailed ? { color: "var(--red-500)" } : undefined}
						disabled={isRunning}
						onClick={handleClick}
					>
						<span
							style={
								isRunning
									? {
											background:
												"linear-gradient(90deg, #6b7280 25%, #e5e7eb 50%, #6b7280 75%)",
											backgroundSize: "200% 100%",
											backgroundClip: "text",
											WebkitBackgroundClip: "text",
											color: "transparent",
											animation: "translate-shimmer 3.1s linear infinite",
										}
									: undefined
							}
						>
							{label}
						</span>
					</Button>
				</>
			</Tooltip>
		</>
	)
}
